/**
 * @fileoverview Standardized row-action and bulk-action builders for DataGrid.
 * @module ui-admin/actions/actionBuilders
 *
 * Responsibilities: - Produce `DataGridRowAction` / `BulkAction` objects with the
 *                     standard icon, label, id, and variant for the actions every
 *                     grid shares (view, edit, duplicate, archive, delete, export).
 *                   - Keep the triple-dot row menu and the multi-select bulk bar
 *                     consistent across all GridPageShell grids without each page
 *                     re-declaring identical action literals.
 * Dependencies:     - lucide-react icons; DataGrid row/bulk action types.
 * Architecture:     - Pure factories — they only assemble action descriptors; the
 *                     caller's `onClick` carries the behavior. No Dexie, no API,
 *                     no mock data.
 * Notes:            - Domain-specific actions are still declared inline by each page;
 *                     these builders cover only the shared standard set.
 *                   - Ids are stable ('view', 'edit', 'duplicate', 'archive',
 *                     'delete', 'export-selected', 'archive-selected',
 *                     'delete-selected') so e2e specs and the parity spec can target
 *                     them uniformly.
 */

import { Eye, Pencil, Copy, Archive, Trash2, Download, CalendarClock, SlidersHorizontal } from 'lucide-react';
import type { DataGridRowAction, BulkAction } from '../components/DataGrid';

// ============================================================
// Row actions (triple-dot menu)
// ============================================================

/**
 * @function makeViewAction
 * Purpose:  Standard "View" row action (Eye icon) that opens the slide-in panel.
 * Inputs:   onClick — invoked with the clicked row.
 * Outputs:  DataGridRowAction with id 'view'.
 */
export function makeViewAction<T>(onClick: (row: T) => void): DataGridRowAction<T> {
  return { id: 'view', label: 'View', icon: Eye, onClick };
}

/**
 * @function makeEditAction
 * Purpose:  Standard "Edit" row action (Pencil icon) that opens the edit flow.
 * Inputs:   onClick — invoked with the clicked row.
 *           opts.disabled — optional per-row disable predicate.
 * Outputs:  DataGridRowAction with id 'edit'.
 */
export function makeEditAction<T>(
  onClick: (row: T) => void,
  opts?: { hidden?: (row: T) => boolean; disabled?: (row: T) => boolean },
): DataGridRowAction<T> {
  return { id: 'edit', label: 'Edit', icon: Pencil, onClick, hidden: opts?.hidden, disabled: opts?.disabled };
}

/**
 * @function makeDuplicateAction
 * Purpose:  Standard "Duplicate" row action (Copy icon).
 * Inputs:   onClick — invoked with the clicked row.
 *           opts.disabled — optional per-row disable predicate.
 * Outputs:  DataGridRowAction with id 'duplicate'.
 */
export function makeDuplicateAction<T>(
  onClick: (row: T) => void,
  opts?: { hidden?: (row: T) => boolean; disabled?: (row: T) => boolean },
): DataGridRowAction<T> {
  return { id: 'duplicate', label: 'Duplicate', icon: Copy, onClick, hidden: opts?.hidden, disabled: opts?.disabled };
}

/**
 * @function makeArchiveAction
 * Purpose:  Standard "Archive" row action (Archive icon). Archive is a soft-delete
 *           tombstone — never a hard delete (project rule: no doc.remove()).
 * Inputs:   onClick — invoked with the clicked row.
 *           opts.hidden — optional per-row hide predicate (e.g. already archived).
 *           opts.disabled — optional per-row disable predicate.
 * Outputs:  DataGridRowAction with id 'archive'.
 */
export function makeArchiveAction<T>(
  onClick: (row: T) => void,
  opts?: { hidden?: (row: T) => boolean; disabled?: (row: T) => boolean },
): DataGridRowAction<T> {
  return { id: 'archive', label: 'Archive', icon: Archive, onClick, hidden: opts?.hidden, disabled: opts?.disabled };
}

/**
 * @function makeDeleteAction
 * Purpose:  Standard "Delete" row action (Trash2 icon, danger variant). Implementations
 *           MUST soft-delete (tombstone) via the local-first service — never
 *           doc.remove().
 * Inputs:   onClick — invoked with the clicked row.
 *           opts.hidden — optional per-row hide predicate.
 *           opts.disabled — optional per-row disable predicate.
 * Outputs:  DataGridRowAction with id 'delete', variant 'danger'.
 */
export function makeDeleteAction<T>(
  onClick: (row: T) => void,
  opts?: { hidden?: (row: T) => boolean; disabled?: (row: T) => boolean },
): DataGridRowAction<T> {
  return {
    id: 'delete',
    label: 'Delete',
    icon: Trash2,
    variant: 'danger',
    onClick,
    hidden: opts?.hidden,
    disabled: opts?.disabled,
  };
}

/**
 * @function makeScheduleAction
 * Purpose:  Standard "Edit Schedule" row action (CalendarClock icon) that opens
 *           the report's schedule editor (navigates to the builder with the
 *           Schedule accordion pre-opened — e.g. `?expand=schedule`).
 * Inputs:   onClick — invoked with the clicked row.
 *           opts.hidden — optional per-row hide predicate.
 *           opts.disabled — optional per-row disable predicate.
 * Outputs:  DataGridRowAction with id 'schedule'.
 */
export function makeScheduleAction<T>(
  onClick: (row: T) => void,
  opts?: { hidden?: (row: T) => boolean; disabled?: (row: T) => boolean },
): DataGridRowAction<T> {
  return { id: 'schedule', label: 'Edit Schedule', icon: CalendarClock, onClick, hidden: opts?.hidden, disabled: opts?.disabled };
}

/**
 * @function makeRunWithParametersAction
 * Purpose:  Standard "Run with Parameters" row action (SlidersHorizontal icon)
 *           that navigates to the report run view, which prompts for parameter
 *           values before rendering. Only meaningful for reports that declare
 *           parameters — hide it per row when `parameters` is empty.
 * Inputs:   onClick — invoked with the clicked row.
 *           opts.hidden — optional per-row hide predicate (e.g. no parameters).
 *           opts.disabled — optional per-row disable predicate.
 * Outputs:  DataGridRowAction with id 'run-with-parameters'.
 */
export function makeRunWithParametersAction<T>(
  onClick: (row: T) => void,
  opts?: { hidden?: (row: T) => boolean; disabled?: (row: T) => boolean },
): DataGridRowAction<T> {
  return { id: 'run-with-parameters', label: 'Run with Parameters', icon: SlidersHorizontal, onClick, hidden: opts?.hidden, disabled: opts?.disabled };
}

// ============================================================
// Bulk actions (multi-select toolbar — DataGrid bulkActions prop)
// ============================================================

/**
 * @function makeExportSelectedAction
 * Purpose:  Standard "Export Selected" bulk action (Download icon) shown in the
 *           multi-select toolbar.
 * Inputs:   onClick — invoked with the array of selected rows.
 * Outputs:  BulkAction with id 'export-selected'.
 */
export function makeExportSelectedAction<T>(
  onClick: (rows: T[]) => void,
  opts?: { disabled?: boolean; tooltip?: string },
): BulkAction<T> {
  return { id: 'export-selected', label: 'Export Selected', icon: Download, onClick, disabled: opts?.disabled, tooltip: opts?.tooltip };
}

/**
 * @function makeArchiveSelectedAction
 * Purpose:  Standard "Archive Selected" bulk action (Archive icon). Archive is a
 *           soft-delete tombstone — never a hard delete.
 * Inputs:   onClick — invoked with the array of selected rows.
 * Outputs:  BulkAction with id 'archive-selected'.
 */
export function makeArchiveSelectedAction<T>(
  onClick: (rows: T[]) => void,
  opts?: { disabled?: boolean; tooltip?: string },
): BulkAction<T> {
  return { id: 'archive-selected', label: 'Archive Selected', icon: Archive, onClick, disabled: opts?.disabled, tooltip: opts?.tooltip };
}

/**
 * @function makeDeleteSelectedAction
 * Purpose:  Standard "Delete Selected" bulk action (Trash2 icon, danger variant).
 *           Implementations MUST soft-delete (tombstone) — never doc.remove().
 * Inputs:   onClick — invoked with the array of selected rows.
 * Outputs:  BulkAction with id 'delete-selected', variant 'danger'.
 */
export function makeDeleteSelectedAction<T>(
  onClick: (rows: T[]) => void,
  opts?: { disabled?: boolean; tooltip?: string },
): BulkAction<T> {
  return { id: 'delete-selected', label: 'Delete Selected', icon: Trash2, variant: 'danger', onClick, disabled: opts?.disabled, tooltip: opts?.tooltip };
}
